import React, { useState } from "react";
import { SearchCheck, Sparkles, AlertTriangle, CheckCircle, HelpCircle, FileText, ArrowRight, RefreshCw, Layers } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../../config/featureFlags";

interface OpportunityAnalystToolProps {
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  savedEmployers?: any[];
  userPreferences?: any;
  onOpenOutreachForEmployer?: (employer: any) => void;
  theme?: "light" | "dark";
  providerHealth?: { status: string; outageReason?: string | null };
  onProviderError?: (err: any) => void;
}

export default function OpportunityAnalystTool({
  apiFetch,
  savedEmployers = [],
  userPreferences,
  onOpenOutreachForEmployer,
  theme = "light",
  providerHealth,
  onProviderError
}: OpportunityAnalystToolProps) {
  const isDark = theme === "dark";
  const isCircuitOpen = providerHealth?.status === "outage";

  const [selectedEmployerId, setSelectedEmployerId] = useState<string>(
    savedEmployers[0]?.id || "custom"
  );

  const [customName, setCustomName] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [customJobTitle, setCustomJobTitle] = useState("");
  const [customJobDesc, setCustomJobDesc] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<any | null>(null);

  const handleRunAnalysis = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading || isCircuitOpen) return;

    if (!AI_FEATURES_ENABLED) {
      setError("AI features are currently disabled.");
      return;
    }

    setIsLoading(true);
    setError(null);

    let selectedEntity: any = {};

    if (selectedEmployerId !== "custom") {
      const found = savedEmployers.find((e) => String(e.id) === String(selectedEmployerId));
      if (found) {
        selectedEntity = {
          id: found.id,
          name: found.name,
          category: found.category || found.type,
          address: found.address || found.vicinity,
          website: found.website || found.url,
          phone: found.phone || found.formatted_phone_number,
          jobTitle: found.jobTitle || found.activeVacancyTitle || "General Role",
          jobDescription: found.jobDescription || found.notes || "",
          notes: found.notes || "",
          hiringSignals: found.opportunitySignals || found.hiringSignals || {},
          websiteAudit: found.websiteAudit || {}
        };
      }
    } else {
      selectedEntity = {
        name: customName || "Target Employer",
        category: customCategory || "Local Business",
        jobTitle: customJobTitle || "Target Role",
        jobDescription: customJobDesc || "",
        address: userPreferences?.location || "Local",
        website: "",
        phone: ""
      };
    }

    try {
      const res = await apiFetch("/api/ai/opportunity-analyst", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selectedEntity,
          userProfile: {
            skills: userPreferences?.skills || [],
            experience: userPreferences?.experience || "General job seeker",
            travelMaxMiles: userPreferences?.radiusMiles || 25,
            schedule: userPreferences?.schedule || "Flexible",
            payExpectation: userPreferences?.payExpectation || "Market competitive"
          }
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setAnalysis(data.data);
    } catch (err: any) {
      console.error("Opportunity Analyst Error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  const getAssessmentColor = (assessment: string) => {
    switch (assessment) {
      case "Strong opportunity":
        return "bg-emerald-500 text-white border-emerald-600";
      case "Worth investigating":
        return "bg-indigo-600 text-white border-indigo-700";
      case "Possible opportunity":
        return "bg-amber-500 text-slate-950 border-amber-600";
      case "Low-priority opportunity":
        return "bg-slate-700 text-slate-200 border-slate-800";
      case "Insufficient information":
      default:
        return "bg-amber-700 text-white border-amber-800";
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header & Selector */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2.5 bg-violet-50 dark:bg-violet-950/60 text-violet-600 dark:text-violet-400 rounded-xl">
            <SearchCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">AI Opportunity Analyst</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Evaluates job postings & employer profiles objectively, detailing fit factors, risks, unknown information, and evidence.
            </p>
          </div>
        </div>

        <form onSubmit={handleRunAnalysis} className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
              Select Employer to Analyze
            </label>
            <select
              disabled={isLoading || isCircuitOpen}
              value={selectedEmployerId}
              onChange={(e) => setSelectedEmployerId(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {savedEmployers.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} — {emp.category || emp.industry || "Saved Business"}
                </option>
              ))}
              <option value="custom">+ Analyze Custom Business or Job</option>
            </select>
          </div>

          {selectedEmployerId === "custom" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">Employer Name</label>
                <input
                  type="text"
                  disabled={isLoading || isCircuitOpen}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Oakridge Dental Care"}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">Business Category</label>
                <input
                  type="text"
                  disabled={isLoading || isCircuitOpen}
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Healthcare, Retail, Accounting"}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">Job Title (if known)</label>
                <input
                  type="text"
                  disabled={isLoading || isCircuitOpen}
                  value={customJobTitle}
                  onChange={(e) => setCustomJobTitle(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Receptionist / Front Desk"}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">Job Details or Requirements</label>
                <input
                  type="text"
                  disabled={isLoading || isCircuitOpen}
                  value={customJobDesc}
                  onChange={(e) => setCustomJobDesc(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Full-time, patient scheduling, billing support"}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || isCircuitOpen}
            className="w-full sm:w-auto px-6 py-3 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-400 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Evaluating Evidence & Fit...</span>
              </>
            ) : isCircuitOpen ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-300" />
                <span>AI Service Temporarily Unavailable</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>Analyze Opportunity</span>
              </>
            )}
          </button>
        </form>
      </div>

      {error && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-950 dark:text-amber-100 shadow-xs">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0 flex-1">
            <p className="font-bold text-amber-900 dark:text-amber-200">AI Service Notice</p>
            <p className="leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Output */}
      {analysis && (
        <div className="space-y-6">
          {/* Banner Assessment */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                  AI Assessment Status
                </span>
                <span className={`inline-block mt-1 px-3.5 py-1.5 rounded-xl font-extrabold text-xs border ${getAssessmentColor(analysis.overallAssessment)}`}>
                  {analysis.overallAssessment}
                </span>
              </div>

              {onOpenOutreachForEmployer && (
                <button
                  type="button"
                  onClick={() => {
                    const found = savedEmployers.find((e) => String(e.id) === String(selectedEmployerId));
                    onOpenOutreachForEmployer(found || { name: customName });
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer"
                >
                  <span>Draft Outreach Script</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Recommended Action */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase text-indigo-600 dark:text-indigo-400">
                  Recommended Action:
                </span>
                <span className="text-xs font-extrabold text-slate-900 dark:text-white">
                  {analysis.recommendedAction}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300">{analysis.actionReason}</p>
            </div>
          </div>

          {/* Fit Factors & Concerns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fit Factors */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Why It May Be a Good Fit
              </h3>
              <ul className="space-y-2 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {analysis.fitFactors?.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-500 font-bold">•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Possible Concerns */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-rose-600 dark:text-rose-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Possible Concerns & Mismatches
              </h3>
              <ul className="space-y-2 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {analysis.concerns?.map((c: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-rose-500 font-bold">•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Unknown Information & Investigation Questions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Unknown Info */}
            <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-amber-900 dark:text-amber-300 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-amber-600" /> Unknown Information
              </h3>
              <p className="text-[11px] text-amber-800 dark:text-amber-300">
                Facts that cannot be confirmed from available data (not automatically negative):
              </p>
              <ul className="space-y-1.5 text-xs text-amber-900 dark:text-amber-200 font-medium">
                {analysis.unknownInformation?.map((u: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold">•</span>
                    <span>{u}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Questions to Investigate */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Questions for Research or Outreach
              </h3>
              <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {analysis.investigationQuestions?.map((q: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-indigo-500 font-bold">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Evidence and Sources */}
          {analysis.evidenceAndSources && analysis.evidenceAndSources.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Underlying Evidence & Data Sources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {analysis.evidenceAndSources.map((e: { claim: string; sourceData: string }, i: number) => (
                  <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                    <span className="text-xs font-extrabold text-slate-900 dark:text-white block">{e.claim}</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-1">Source: {e.sourceData}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
